/**
 * Copy/Move to target
 *
 * > folder browser to select the target folder
 *
 * 2 actions :
 * - **create new folder** : (default) when not provide `file` input
 * - **rename a selected file** : when `file` input is provided
 */
import {
  AfterViewInit,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormGroupDirective, ReactiveFormsModule } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { filter, map } from 'rxjs';

import {
  BreadcrumbComponent,
  BreadcrumbItem,
  ButtonComponent,
  IconComponent,
  InputComponent,
  ModalComponent,
  ModalService,
  Nullable,
  OptionsGroupComponent,
  SkeletonComponent,
  StatusIndicatorComponent,
  ValueOf,
} from '@evc/web-components';
import { EvcFormService } from '@shared/reactive-form/reactive-form.service';
import { DataToFormModel } from '@shared/reactive-form/types/reactive-form.type';
import { I18nService, TranslatePipe } from '@shared/services/i18n/i18n.service';

import { FileItemComponent } from '@app/components/file-item/file-item.component';
import { loadFolders } from '@app/store/actions/browse-target/browse-target.action';
import { areFoldersLoading, getBreadcrumb, getFolders, getRouterData } from '@app/store/selectors';
import type { FileItem } from '@app/types/file-item.type';

export type MoreSelectedItemTag = { id: string; label: string };

export const browseTargetActions = {
  COPY: 'copy',
  MOVE: 'move',
} as const;
export type BrowseTargetAction = ValueOf<typeof browseTargetActions>;

export type BrowseTargetFormData = {
  targetFolderId: Nullable<string>;
};
export type BrowseTargetFormModel = DataToFormModel<BrowseTargetFormData>;

/** display max n/n+1 items in selected list (if n+2 show badge more) */
export const BrowseTargetMaxSelectedItemsShown = 2;

/** id of the synthetic base/root crumb */
const BREADCRUMB_BASE_ID = 'base';

/** build the path from the current folder up to the root by walking parentId */
function buildBreadcrumbPath(
  map: Record<string, BreadcrumbItem>,
  id: Nullable<string>,
  acc: BreadcrumbItem[] = [],
): BreadcrumbItem[] {
  const node = id ? map[id] : undefined;
  if (!node) return acc;

  return buildBreadcrumbPath(map, node.parentId ?? null, [node, ...acc]);
}

@Component({
  selector: 'browse-target',
  templateUrl: './browse-target.component.html',
  styleUrls: ['./browse-target.component.scss'],
  imports: [
    ReactiveFormsModule,
    ModalComponent,
    BreadcrumbComponent,
    StatusIndicatorComponent,
    InputComponent,
    OptionsGroupComponent,
    IconComponent,
    ButtonComponent,
    FileItemComponent,
    SkeletonComponent,
    TranslatePipe,
  ],
  providers: [EvcFormService],
})
export class BrowseTargetModalComponent extends ModalComponent implements OnInit, AfterViewInit {
  action = input.required<BrowseTargetAction>();
  items = input.required<FileItem[]>();

  form!: FormGroup<BrowseTargetFormModel>;
  formDirective = viewChild.required(FormGroupDirective);

  // ng limitation : must use child use child ModalComponent to provide ng-containers
  // so in this parent instance we will need to forward subject emitions
  childModal = viewChild.required(ModalComponent);
  breadcrumb = signal<BreadcrumbItem[]>([]);

  readonly #modalService = inject(ModalService);
  readonly #i18nService = inject(I18nService);
  readonly #formService = inject(EvcFormService<BrowseTargetFormData, boolean>);
  readonly #formBuilder = inject(FormBuilder);
  readonly #store = inject(Store);
  readonly #destroyRef = inject(DestroyRef);

  #isMove = computed(() => this.action() === browseTargetActions.MOVE);

  protected i18n = computed(() => {
    const isMove = this.#isMove();

    const action = isMove ? 'actions.moveTo' : 'actions.copyTo';

    return {
      title: `${action}.label`,
      action: `${action}.action`,
      cancel: 'actions.cancel',
    };
  });

  protected itemIds = computed<string[]>(() => this.items().map(({ id }) => id));

  protected listedSelectedItems = computed<(FileItem | MoreSelectedItemTag)[]>(() => {
    const items = this.items();

    if (items.length <= BrowseTargetMaxSelectedItemsShown + 1) {
      return items;
    }

    const visible = items.slice(0, BrowseTargetMaxSelectedItemsShown);
    const othersCount = items.length - BrowseTargetMaxSelectedItemsShown;

    return [
      ...visible,
      {
        id: 'more-selected',
        label: this.#i18nService.t('labels.nMoreItems', { count: othersCount }),
      },
    ];
  });

  readonly skeletonRows = Array(3);

  protected isLoading = toSignal(this.#store.pipe(select(areFoldersLoading)));
  protected folders = toSignal<FileItem[]>(this.#store.pipe(select(getFolders)));

  protected baseFolder = toSignal<string>(
    this.#store.pipe(
      select(getRouterData),
      map<{ base: string }, string>(({ base }) => base),
      filter(Boolean),
      map(this.#i18nService.t.bind(this.#i18nService)),
    ),
  );

  protected currentFolderId = computed<Nullable<string>>(() => this.items()[0]?.parentId ?? null);

  protected breadcrumbList = computed<BreadcrumbItem[]>(() => {
    const baseName = this.baseFolder() ?? '';
    const items = this.breadcrumb();
    const currentId = this.parentFolderId();
    const map = Object.fromEntries(items.map((item) => [item.id, item]));

    return [{ id: BREADCRUMB_BASE_ID, name: baseName }, ...buildBreadcrumbPath(map, currentId)];
  });

  constructor() {
    super();

    effect(() => {
      const { parentId } = this.items()?.[0] ?? {};
      untracked(() => {
        if (!parentId) return;
        this.parentFolderId.set(parentId);
      });
    });
  }

  protected displayedFolders = computed<FileItem[]>(() => {
    const folders: FileItem[] = this.folders() ?? [];
    const parentFolderId: Nullable<string> = this.parentFolderId() ?? null;
    const itemIds: string[] = this.itemIds();

    return untracked(() => {
      const levelFolders = folders
        .filter(({ parentId }) => parentId === parentFolderId)
        .map<
          FileItem & {
            icon: 'folder';
            disabled?: true;
            hasChildren: boolean;
          }
        >((folder) => ({
          ...folder,
          icon: 'folder',
          disabled: itemIds.includes(folder.id!) || undefined,
          hasChildren: folders.map(({ parentId }) => parentId).indexOf(folder.id) >= 0,
        }));

      return levelFolders;
    });
  });

  /** folder selected on breadcrump */
  parentFolderId = signal<Nullable<string>>(null);

  submit = this.#formService.submit.bind(this.#formService);

  ngOnInit(): void {
    // radio must have some value...
    const targetFolderId = this.parentFolderId() ?? this.currentFolderId() ?? 'base';
    const control = this.#formService.controlFactory(this.#formBuilder, { targetFolderId });
    this.form = this.#formBuilder.group<BrowseTargetFormModel>({
      targetFolderId: control('targetFolderId', [], false),
    });

    this.#store.pipe(select(getBreadcrumb)).subscribe((items) => {
      // getBreadcrumb's defensive `!!state && state.value` chain can resolve to `false`
      // (not just null/undefined) when the breadcrumb feature slice isn't registered yet.
      this.breadcrumb.set(Array.isArray(items) ? items : []);
    });

    this.#store.dispatch(loadFolders({ parent: { id: this.currentFolderId() } }));
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();

    this.#bindFormService();
    this.#bindChildModalSubject();
  }

  protected submit$(data: Partial<BrowseTargetFormData>): Observable<any> {
    // radio does not accept null value
    const targetFolderId = (() => (data.targetFolderId === 'base' ? null : data.targetFolderId))();

    setTimeout(() => {
      this.subject.next(targetFolderId);
    }, 0);

    return this.subject;
  }

  protected onCrumbSelected(item: BreadcrumbItem): void {
    if (item.id === BREADCRUMB_BASE_ID) {
      this.selectBase();
      return;
    }

    this.selectAndEnterFolder(item);
  }

  protected selectBase(): void {
    const base = this.baseFolder();
    this.parentFolderId.set(null);
    this.form.patchValue({ targetFolderId: 'base' });

    this.#store.dispatch(loadFolders({ parent: { base } }));
  }

  protected selectFolder(folder: Partial<FileItem>): Nullable<string> {
    const targetFolderId = folder.id || 'base';
    this.form.patchValue({ targetFolderId });

    return targetFolderId;
  }

  protected selectAndEnterFolder(folder: Partial<FileItem>): void {
    const targetFolderId = this.selectFolder(folder);
    this.parentFolderId.set(targetFolderId);

    this.#store.dispatch(loadFolders({ parent: { id: targetFolderId } }));

    const breadcrumbItems = this.breadcrumb();
    const existingItem = breadcrumbItems.find((item) => item.id === folder.id);

    if (!existingItem) {
      this.breadcrumb.set([
        ...breadcrumbItems,
        {
          id: folder.id!,
          parentId: folder.parentId ?? undefined,
          name: folder.name!,
        },
      ]);
    }
  }

  #bindChildModalSubject(): void {
    const child = this.childModal();
    if (child && child !== this) {
      // Forward all child modal events to parent subject
      child.subject.subscribe({
        next: (value) => this.subject.next(value),
        error: (error) => this.subject.error(error),
        complete: () => this.subject.complete(),
      });
    }
  }

  #bindFormService(): void {
    this.#formService.bind({
      formGroup: this.form,
      formDirective: this.formDirective(),
      destroyRef: this.#destroyRef,
      onSuccess: this.#modalService.close.bind(this.#modalService),
      catchErrors: false,
      submitHook: this.submit$.bind(this),
    });
  }
}
